/* Copyright (c) 2015-present, salesforce.com, inc. All rights reserved */
/* Licensed under BSD 3-Clause - see LICENSE.txt or git.io/sfdc-license */

// ### React
import React from 'react';
import PropTypes from 'prop-types';
// ### classNames
import classNames from 'classnames';
// ### find
import find from 'lodash.find';
// ## Children
import {Button, Checkbox} from '@salesforce/design-system-react';
// ## Constants
import {DATA_TABLE_CELL, DATA_TABLE_ROW, DATA_TABLE_ROW_ACTIONS,} from '@salesforce/design-system-react/utilities/constants';
import {Log} from "../../../log";

// Safely get the length of an array, returning 0 for invalid input.
const count = (array) => (Array.isArray(array) ? array.length : 0);


/**
 * Used internally, provides row rendering to the DataTable.
 */
class DataTableRow extends React.Component {

    // ### Display Name
// Always use the canonical component name as the React display name.
    static displayName = DATA_TABLE_ROW;

    state = {
        showDetails: false,
    };

    isSelected = () => {
        return !!find(this.props.selection, this.props.item);
    };

    handleToggle = (e, {checked}) => {
        return this.props.onToggle(this.props.item, checked, e);
    };

    shouldComponentUpdate(nextProps, nextState) {
        return true;
    }

    // ### Render
    render() {
        const isSelected = this.isSelected();
        const canShowDetails = this.props.detailComponent !== undefined;
        let totalCols = count(this.props.columns);
        if (canShowDetails) {
            totalCols++;
        }
        if (this.props.canSelectRows) {
            totalCols++;
        }

        // i18n
        return (
            <React.Fragment>
                <tr
                    className={classNames({
                        'slds-hint-parent': this.props.rowActions,
                        'slds-is-selected': this.props.canSelectRows && isSelected,
                    })}
                >
                    {canShowDetails ? (
                        <td
                            role={this.props.fixedLayout ? 'gridcell' : null}
                            className="slds-text-align--left"
                            data-label="Details"
                            style={{width: '3.25rem'}}
                        >
                            <Button
                                assistiveText={{icon: this.props.assistiveText.showDetails}}
                                iconCategory="utility"
                                iconName={this.state.showDetails ? "chevronup" : "chevrondown"}
                                iconSize="small"
                                iconVariant="bare"
                                onClick={() => {
                                    this.setState({showDetails: !this.state.showDetails});
                                }}
                                variant="icon"
                            />
                        </td>
                    ) : null}
                    {this.props.canSelectRows ? (
                        <td
                            role={this.props.fixedLayout ? 'gridcell' : null}
                            className="slds-text-align--right"
                            data-label="Select Row"
                            style={{width: '3.25rem'}}
                        >
                            <Checkbox
                                assistiveText={{
                                    label: this.props.assistiveText.selectRow,
                                }}
                                checked={isSelected}
                                id={`${this.props.id}-SelectRow`}
                                name="SelectRow"
                                onChange={this.handleToggle}
                            />
                        </td>
                    ) : null}
                    {this.props.columns.map((column) => {
                        const Cell = column.Cell;
                        let id = column.props.property || column.props.id || column.props.label;
                        const cellId = `${this.props.id}-${DATA_TABLE_CELL}-${id}`;

                        //Log.Debug("Column.props", column.props, cellId);
                        let content = this.props.item[column.props.property];
                        if (content === null || content === undefined) {
                            content = "";
                        }
                        if (typeof content === "boolean") {
                            // TODO: Or maybe render as: <Checkbox checked={content} readOnly/>
                            content = `${content}`;
                        }
                        return (
                            <Cell
                                {...column.props}
                                className={column.props.truncate ? 'slds-truncate' : null}
                                fixedLayout={this.props.fixedLayout}
                                rowHeader={column.props.primaryColumn}
                                id={cellId}
                                item={this.props.item}
                                key={cellId}
                                width={column.props.width}
                            >{content}</Cell>
                        );
                    })}
                    {this.props.rowActions
                        ? React.cloneElement(this.props.rowActions, {
                            id: `${this.props.id}-${DATA_TABLE_ROW_ACTIONS}`,
                            item: this.props.item,
                        })
                        : null}
                </tr>
                {canShowDetails && this.state.showDetails ?
                    <React.Fragment>
                        <tr>
                            <td colSpan={totalCols} style={{backgroundColor: "#ffffff"}}>
                                {this.props.detailComponent ? this.props.detailComponent(this.props.item) : "missing 'props.detailComponent'"}
                            </td>
                        </tr>
                        <tr style={{height: '0px'}}/>
                    </React.Fragment>
                    : null}
            </React.Fragment>
        );
    }
}


// ### Prop Types
DataTableRow.propTypes = {
    assistiveText: PropTypes.shape({
        actionsHeader: PropTypes.string,
        columnSort: PropTypes.string,
        columnSortedAscending: PropTypes.string,
        columnSortedDescending: PropTypes.string,
        selectAllRows: PropTypes.string,
        selectRow: PropTypes.string,
        showDetails: PropTypes.string,
    }),
    detailComponent: PropTypes.func,
    canSelectRows: PropTypes.bool,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            Cell: PropTypes.func,
            props: PropTypes.object,
        })
    ),
    /**
     * Use this if you are creating an advanced table (selectable, sortable, or resizable rows)
     */
    fixedLayout: PropTypes.bool,
    id: PropTypes.string.isRequired,
    item: PropTypes.object.isRequired,
    onToggle: PropTypes.func,
    rowActions: PropTypes.element,
    selection: PropTypes.array,
};

export default DataTableRow;
